import { Pipe, PipeTransform } from '@angular/core';
import { AssessmentTemplateEnum } from 'src/app/api/models/assessments/assessment.enum';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { AssessmentRegulation } from '../models/assessment-regulation.interface';

/** Read-out placeholder for "no regulation", matching the template builder's own read-out. */
const NO_REGULATION = '-';

@Pipe({
    standalone: true,
    name: 'aiAssessmentTemplateName'
})
export class AiAssessmentTemplateNamePipe implements PipeTransform {

    constructor(
        private contentPipe: ContentPipe,
    ) { }

    /**
     * Resolves a type/regulation (given as enum key or value) to its display name. The `regulations`
     * CMS list is the single source of truth (keyed by `value` = the enum value) so editing a
     * regulation's display name flows to every read-out.
     *
     * This read-out IS the Regulation field, and there is no "General" regulation: a blank (`Empty`)
     * template renders "-" like the template it was created from, and an unrecognised type renders "-"
     * rather than inheriting the `Empty` label. Types that are in the `types` map but not in
     * `regulations` (Vendor, PA, …) keep their own name — mirroring
     * `AiAssessmentsTemplateBuilderComponent.regulationName` exactly.
     */
    transform(key: string): string {
        const regulations = (this.contentPipe.transform('ai-assessments.regulations') as AssessmentRegulation[]) ?? [];
        const types: {[k: string]: string} = this.contentPipe.transform('ai-assessments.types');
        const keys = Object.keys(AssessmentTemplateEnum);
        const values = Object.values(AssessmentTemplateEnum);

        const findByKey = keys.find(k => k?.toLowerCase() === key?.toLowerCase() || AssessmentTemplateEnum[k]?.toLowerCase() === key?.toLowerCase());
        const findByValue = values.find(v => v?.toLowerCase() === key?.toLowerCase());
        const value = findByKey ? AssessmentTemplateEnum[findByKey] : (findByValue ?? key);

        const regulation = regulations.find(reg => reg.value === value);

        // An unresolvable key shows "-": there is no "General" regulation.
        if (!value || value === AssessmentTemplateEnum.Empty) {
            return NO_REGULATION;
        }
        return regulation?.name ?? types?.[value] ?? NO_REGULATION;
    }
}
